import { useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "/api";

const usePermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================= FETCH PERMISSIONS ================= */
  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      let user = null;

      try {
        user = JSON.parse(localStorage.getItem("user"));
      } catch {
        user = null;
      }

      /* 🔐 NO TOKEN */
      if (!token) {
        setPermissions([]);
        return;
      }

      /* 🔥 SUPER ADMIN BYPASS */
      if (user?.role === "super_admin") {
        setPermissions([{ module: "all", action: "all" }]);
        return;
      }

      /* ✅ SAFE API CALL */
      const res = await fetch(`${API}/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      /* ❗ IMPORTANT: DO NOT WIPE PERMISSIONS ON FAILURE */
      if (!res.ok) {
        console.warn("Permissions API failed:", res.status);
        return; // keep previous permissions
      }

      const data = await res.json();

      /* ================= NORMALIZE ================= */
      let permissionsArray = [];

      if (Array.isArray(data)) {
        permissionsArray = data;
      } else if (Array.isArray(data.permissions)) {
        permissionsArray = data.permissions;
      } else if (Array.isArray(data.data)) {
        permissionsArray = data.data;
      }

      const formatted = permissionsArray.map((p) => ({
        module: (p.module || "").toLowerCase(),
        action: (p.action || "").toLowerCase(),
      }));

      setPermissions(formatted);
    } catch (err) {
      console.error("Permission fetch error:", err);

      // ❗ DO NOT CLEAR permissions on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /* ================= ACCESS CHECK ================= */
  const hasAccess = useCallback(
    (module, action) => {
      if (!module || !action) return false;

      if (permissions.some((p) => p.module === "all")) return true;

      return permissions.some(
        (p) =>
          p.module === module.toLowerCase() &&
          p.action === action.toLowerCase()
      );
    },
    [permissions]
  );

  const canView = useCallback(
    (module) => hasAccess(module, "view"),
    [hasAccess]
  );

  const canCreate = useCallback(
    (module) => hasAccess(module, "create"),
    [hasAccess]
  );

  const canEdit = useCallback(
    (module) => hasAccess(module, "edit"),
    [hasAccess]
  );

  const canDelete = useCallback(
    (module) => hasAccess(module, "delete"),
    [hasAccess]
  );

  const refreshPermissions = () => {
    fetchPermissions();
  };

  return {
    permissions,
    loading,
    hasAccess,
    canView,
    canCreate,
    canEdit,
    canDelete,
    refreshPermissions,
  };
};

export default usePermissions;
